import { useCallback } from 'react';

interface Column {
  key: string;
  header: string;
}

/**
 * Escape a single CSV field value per RFC 4180.
 * Fields containing commas, double quotes, or newlines are wrapped in double quotes
 * and inner double quotes are escaped by doubling ("" → """").
 */
function escapeCsvField(value: string): string {
  if (
    value.includes(',') ||
    value.includes('"') ||
    value.includes('\n') ||
    value.includes('\r')
  ) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/**
 * Convert an array of row objects to an RFC-4180 CSV string.
 * Prepends UTF-8 BOM (\ufeff) for Excel compatibility.
 * Uses CRLF line endings.
 * Null/undefined values become empty strings.
 */
export function rowsToCsv<T extends Record<string, any>>(
  columns: Column[],
  data: T[],
): string {
  const BOM = '\ufeff';

  const header = columns.map((c) => escapeCsvField(c.header)).join(',');

  const body = data.map((row) =>
    columns
      .map((c) => {
        const val = row[c.key];
        const str = val == null ? '' : String(val);
        return escapeCsvField(str);
      })
      .join(','),
  );

  return BOM + [header, ...body].join('\r\n');
}

/**
 * Trigger a browser file download for the given CSV content.
 * Uses Blob + URL.createObjectURL + temporary anchor element.
 * Appends the anchor to the document body for cross-browser compatibility (Firefox).
 */
export function downloadCsv(csvString: string, filename: string): void {
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * React hook that provides an `exportCsv` function for client-side CSV export.
 *
 * @param columns - Column definitions with `key` (row property) and `header` (CSV column title).
 * @param data    - Array of row objects to export.
 * @param filename - Base filename (without extension). Date suffix is appended automatically.
 *
 * @returns `{ exportCsv }` — call to trigger a download of `{filename}-{YYYY-MM-DD}.csv`.
 *
 * @example
 * const { exportCsv } = useCsvExport(columns, data, 'Compras');
 * <button onClick={exportCsv}>Exportar CSV</button>
 */
export function useCsvExport<T extends Record<string, any>>(
  columns: { key: string; header: string }[],
  data: T[],
  filename: string,
): { exportCsv: () => void } {
  const exportCsv = useCallback(() => {
    const csv = rowsToCsv(columns, data);
    const dateStr = new Date().toISOString().slice(0, 10);
    downloadCsv(csv, `${filename.replace(/\.csv$/i, '')}-${dateStr}.csv`);
  }, [columns, data, filename]);

  return { exportCsv };
}
